import type { CommandEnvelope, PlaybackState, PlayerCommand, Track } from '../../contracts/src/index.ts';
import { SlcError, validateControl, validateSource } from './types.ts';

export const uuid = (v: unknown): v is string => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
export const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown, max: number): v is string => typeof v === 'string' && v.length <= max && !/[\r\n\0]/.test(v);
const invalid = () => new SlcError('UNSUPPORTED', 'This playback command is unavailable.');
export function parseCommand(value: unknown): PlayerCommand {
  if (!record(value)) throw invalid();
  const action = value.action;
  switch (action) {
    case 'play': case 'pause': case 'toggle': case 'stop': case 'next': return { action };
    case 'seek': {
      if (typeof value.position !== 'number') throw invalid();
      const command = { action, position: value.position }; validateControl(command); return command;
    }
    case 'seekSession': {
      if (!uuid(value.sessionId) || typeof value.position !== 'number') throw invalid();
      const command = { action, sessionId: value.sessionId, position: value.position }; validateControl(command); return command;
    }
    case 'speed': case 'volume': {
      if (typeof value.value !== 'number') throw invalid();
      const command = { action, value: value.value }; validateControl(command); return command;
    }
    case 'audio': case 'subtitle':
      if (!text(value.trackId, 180)) throw invalid();
      return { action, trackId: value.trackId };
    case 'subtitleDelay': {
      if (!uuid(value.sessionId) || typeof value.value !== 'number') throw invalid();
      const command = { action, sessionId: value.sessionId, value: value.value }; validateControl(command); return command;
    }
    case 'navigate':
      if (!['up', 'down', 'left', 'right', 'select', 'back', 'home'].includes(String(value.key))) throw invalid();
      return { action, key: value.key as Extract<PlayerCommand, { action: 'navigate' }>['key'] };
    case 'load': {
      if (!uuid(value.sessionId) || !text(value.mediaId, 180) || !text(value.videoId, 180) ||
          !text(value.type, 30) || !text(value.title, 300) || !text(value.url, 8192)) throw invalid();
      let url: URL;
      try { url = new URL(value.url); } catch { throw invalid(); }
      if (url.protocol !== 'https:' || url.username || url.password) throw invalid();
      // Neither TV web transport establishes origin-scoped protected headers.
      if (value.headers !== undefined && (!record(value.headers) || Object.keys(value.headers).length))
        throw new SlcError('HEADERS_UNSUPPORTED', 'Choose a source that does not require protected headers on this TV.');
      if (value.startPosition !== undefined && typeof value.startPosition !== 'number') throw invalid();
      if (value.continuationOf !== undefined && !uuid(value.continuationOf)) throw invalid();
      if (value.subtitles !== undefined && (!Array.isArray(value.subtitles) || value.subtitles.length > 150)) throw invalid();
      const subtitles = (value.subtitles as unknown[] | undefined)?.map(s => {
        if (!record(s) || !text(s.id, 180) || !text(s.lang, 64) || !text(s.url, 8192)) throw invalid();
        let u: URL; try { u = new URL(s.url); } catch { throw invalid(); }
        if (u.protocol !== 'https:' || u.username || u.password) throw invalid();
        return { id: s.id, lang: s.lang, url: s.url };
      });
      const command = { action, sessionId: value.sessionId, mediaId: value.mediaId, videoId: value.videoId,
        type: value.type, title: value.title, url: value.url, startPosition: value.startPosition as number | undefined, subtitles,
        ...(value.continuationOf === undefined ? {} : { continuationOf: value.continuationOf as string }) };
      validateSource(command); return command;
    }
    default: throw invalid();
  }
}

export function parseEnvelope(value: unknown, deviceId: string, now: number): CommandEnvelope {
  if (!record(value) || value.version !== 1 || value.kind !== 'command' || !uuid(value.id) || value.deviceId !== deviceId)
    throw invalid();
  if (typeof value.expiresAt !== 'number' || !Number.isSafeInteger(value.expiresAt) || value.expiresAt <= now || value.expiresAt > now + 30000)
    throw new SlcError('COMMAND_EXPIRED', 'This remote action expired. Try again.');
  return { version: 1, kind: 'command', id: value.id, deviceId, expiresAt: value.expiresAt, command: parseCommand(value.command) };
}

/** Explicit wire projection: never spread an adapter object into a controller report. */
export function publicPlayback(s: PlaybackState, revision: number): PlaybackState {
  if (!uuid(s.sessionId) || !text(s.mediaId, 180) || !text(s.videoId, 180) || !text(s.type, 30) || !text(s.title, 300) ||
      ![s.position, s.duration].every(v => Number.isFinite(v) && v >= 0 && v <= 604800) || typeof s.paused !== 'boolean' ||
      !text(s.engine, 30)) throw invalid();
  const tracks = (values: Track[] | undefined, max: number) => values?.filter(t => text(t.id, 180))
    .slice(0, max).map(t => ({ id: t.id, label: String(t.label ?? '').slice(0, 180),
      ...(typeof t.language === 'string' ? { language: t.language.slice(0, 30) } : {}), selected: t.selected === true }));
  const result: PlaybackState = { sessionId: s.sessionId, revision, mediaId: s.mediaId, videoId: s.videoId, type: s.type,
    title: s.title, position: s.position, duration: s.duration, paused: s.paused, engine: s.engine,
    audioTracks: tracks(s.audioTracks, 80), subtitleTracks: tracks(s.subtitleTracks, 150) };
  if (typeof s.speed === 'number' && Number.isFinite(s.speed) && s.speed >= .25 && s.speed <= 4) result.speed = s.speed;
  if (typeof s.volume === 'number' && Number.isFinite(s.volume) && s.volume >= 0 && s.volume <= 1) result.volume = s.volume;
  if (typeof s.subtitleDelay === 'number' && Number.isFinite(s.subtitleDelay) && s.subtitleDelay >= -10 && s.subtitleDelay <= 10)
    result.subtitleDelay = s.subtitleDelay;
  if (s.sessionSeek === true && !s.local) result.sessionSeek = true;
  // Room messages are limited to32768 UTF-8 bytes, including the envelope. Keep
  // selected tracks; discard unselected tail entries only when that limit binds.
  const encoder = new TextEncoder();
  while (encoder.encode(JSON.stringify(result)).length > 30000) {
    const list = [result.subtitleTracks, result.audioTracks].find(a => a?.some(t => !t.selected));
    if (!list) throw invalid();
    let i = list.length - 1; while (list[i].selected) i--;
    list.splice(i, 1);
  }
  return result;
}
